import { jsxs, jsx } from 'react/jsx-runtime';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import cn from 'classnames';
import 'react';
import 'throttle-debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const createBemBlockBuilder = (blocks)=>{
    const cleanedBlocks = blocks.reduce((reviewedBlocks, block)=>block ? reviewedBlocks.concat(block.trim().split(" ")) : reviewedBlocks, []);
    return (elementOrModifier = "")=>cleanedBlocks.reduce((classList, block)=>cn(classList, `${block}${elementOrModifier}`), "");
};

const BASE_CLASS_NAME$1 = "eb-page-pagination-item";
const PaginationItem = ({ itemType = "button", className, title, icon, isSelected = false, disabled = false, ariaLabel, onClick })=>{
    const getBEMBlocksWith = createBemBlockBuilder([
        BASE_CLASS_NAME$1,
        className
    ]);
    const handleClickLink = (e)=>{
        e.preventDefault();
        !disabled && (onClick === null || onClick === void 0 ? void 0 : onClick());
    };
    return /*#__PURE__*/ jsxs("li", {
        className: getBEMBlocksWith(),
        children: [
            itemType === "button" && /*#__PURE__*/ jsxs("button", {
                className: cn(getBEMBlocksWith("__button"), disabled && getBEMBlocksWith("__button--disabled"), isSelected && getBEMBlocksWith("__button--selected")),
                onClick: ()=>onClick === null || onClick === void 0 ? void 0 : onClick(),
                type: "button",
                disabled: disabled,
                "aria-disabled": disabled,
                "aria-label": ariaLabel,
                "aria-current": isSelected,
                children: [
                    icon && /*#__PURE__*/ jsx(FontAwesomeIcon, {
                        icon: icon
                    }),
                    title
                ]
            }),
            itemType === "link" && // href is kept to keep the element acessible from keyboard
            // but the click is handled by handleClickLink
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            /*#__PURE__*/ jsxs("a", {
                href: !disabled ? "#" : undefined,
                tabIndex: !isSelected && !disabled ? 0 : -1,
                className: cn(getBEMBlocksWith("__link"), disabled && getBEMBlocksWith("__link--disabled"), isSelected && getBEMBlocksWith("__link--selected")),
                onClick: handleClickLink,
                "aria-label": ariaLabel,
                "aria-current": isSelected,
                "aria-disabled": disabled,
                children: [
                    icon && /*#__PURE__*/ jsx(FontAwesomeIcon, {
                        icon: icon
                    }),
                    title
                ]
            })
        ]
    });
};

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const BASE_CLASS_NAME = "eb-page-pagination";
const DOTS = "\u2026"; //ellipsis
const ITEMS_COUNT = 7;
const PREVIOUS_ARIA_LABEL = "Previous page";
const NEXT_ARIA_LABEL = "Next page";
const PAGINATION_ARIA_LABEL = "Pagination control";
const defaultPageAriaLabel = (page)=>`Page ${page}`;
const Pagination = (_param)=>{
    var { className, itemType = "button", activePage, count, previousPageLabel = PREVIOUS_ARIA_LABEL, nextPageLabel = NEXT_ARIA_LABEL, paginationAriaLabel = PAGINATION_ARIA_LABEL, pageAriaLabel = defaultPageAriaLabel, onSelect } = _param, rest = _object_without_properties(_param, [
        "className",
        "itemType",
        "activePage",
        "count",
        "previousPageLabel",
        "nextPageLabel",
        "paginationAriaLabel",
        "pageAriaLabel",
        "onSelect"
    ]);
    //filter wrong values
    const pagesCount = count > 0 ? count : 1;
    let currentPage;
    if (activePage < 1) {
        currentPage = 1;
    } else {
        currentPage = activePage < pagesCount ? activePage : pagesCount;
    }
    const getBEMBlocksWith = createBemBlockBuilder([
        BASE_CLASS_NAME,
        className
    ]);
    const buildItems = ()=>{
        const calculateSiblings = ()=>{
            const siblings = [
                currentPage
            ];
            let leftSibling = currentPage;
            let rightSibling = currentPage;
            let hasNoChanges;
            for(let i = 1; i < ITEMS_COUNT;){
                hasNoChanges = true;
                if (leftSibling > 1) {
                    siblings.unshift(--leftSibling);
                    hasNoChanges = false;
                    i++;
                }
                if (rightSibling < pagesCount) {
                    siblings.push(++rightSibling);
                    hasNoChanges = false;
                    i++;
                }
                if (hasNoChanges) {
                    break;
                }
            }
            return siblings;
        };
        const items = calculateSiblings();
        //process left items
        let leftItems = [];
        if (items[0] !== 1) {
            items.shift();
            items.shift();
            leftItems = [
                buildNumber(1),
                buildDots("left_ellipsis")
            ];
        }
        //process right items
        let rightItems = [];
        if (items[items.length - 1] !== pagesCount) {
            items.pop();
            items.pop();
            rightItems = [
                buildDots("right_ellipsis"),
                buildNumber(pagesCount)
            ];
        }
        //process middle items
        const middleItems = items.map((num)=>buildNumber(num, num === currentPage));
        //merge 3 arrays into 1
        return [
            ...leftItems,
            ...middleItems,
            ...rightItems
        ];
    };
    const buildDots = (key)=>/*#__PURE__*/ jsx("li", {
            children: /*#__PURE__*/ jsx("div", {
                className: getBEMBlocksWith("__dots"),
                children: DOTS
            })
        }, key);
    const buildNumber = (num, isSelected = false)=>/*#__PURE__*/ jsx(PaginationItem, {
            itemType: itemType,
            className: getBEMBlocksWith(isSelected ? "__current_page" : "__page"),
            title: num.toLocaleString(),
            onClick: ()=>onSelect(num),
            isSelected: isSelected,
            disabled: isSelected,
            ariaLabel: pageAriaLabel(num)
        }, num.toLocaleString());
    const buildLeftArrow = (disabled)=>/*#__PURE__*/ jsx(PaginationItem, {
            itemType: itemType,
            className: getBEMBlocksWith("__first"),
            icon: faChevronLeft,
            onClick: ()=>{
                if (currentPage > 1) {
                    onSelect(currentPage - 1);
                }
            },
            disabled: disabled,
            ariaLabel: previousPageLabel
        }, "previous");
    const buildRightArrow = (disabled)=>/*#__PURE__*/ jsx(PaginationItem, {
            itemType: itemType,
            className: getBEMBlocksWith("__last"),
            icon: faChevronRight,
            onClick: ()=>{
                if (currentPage < pagesCount) {
                    onSelect(currentPage + 1);
                }
            },
            disabled: disabled,
            ariaLabel: nextPageLabel
        }, "next");
    //add left and right arrows
    const elements = [
        buildLeftArrow(currentPage === 1),
        ...buildItems(),
        buildRightArrow(currentPage === pagesCount)
    ];
    return /*#__PURE__*/ jsx("nav", _object_spread_props(_object_spread({
        "aria-label": paginationAriaLabel,
        className: getBEMBlocksWith()
    }, rest), {
        children: /*#__PURE__*/ jsx("ul", {
            className: getBEMBlocksWith("__items"),
            children: elements
        })
    }));
};

export { Pagination as default };
//# sourceMappingURL=pagination-esm.js.map
